import { useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { TradePicker } from "@/components/trades/TradePicker";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const OrgSetup = () => {
  const { user } = useAuth();
  const [step, setStep] = useState<"name" | "trades">("name");
  const [loading, setLoading] = useState(false);
  const [orgName, setOrgName] = useState("");
  const [tradeCodes, setTradeCodes] = useState<string[]>([]);

  const handleNameNext = (e: React.FormEvent) => {
    e.preventDefault();
    if (!orgName.trim()) {
      toast.error("Organization name is required");
      return;
    }
    setStep("trades");
  };

  const handleCreate = async () => {
    setLoading(true);
    try {
      if (!user) throw new Error("Not authenticated");
      if (tradeCodes.length === 0) throw new Error("Pick at least one trade");

      const { data, error } = await supabase.functions.invoke("complete-org-onboarding", {
        body: { orgName: orgName.trim(), tradeCodes },
      });

      if (error) {
        const details = "context" in error && error.context
          ? await error.context.json().catch(() => null)
          : null;
        throw new Error(details?.error || error.message || "Failed to create organization");
      }
      if (data?.error) throw new Error(data.error);

      toast.success("Organization created!");
      window.location.assign("/");
    } catch (error: any) {
      console.error("Error creating organization:", error);
      toast.error(error.message || "Failed to create organization");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-muted/20 to-background p-4">
      <Card className="w-full max-w-2xl">
        <CardHeader>
          <CardTitle>Welcome to RoofGenius AI</CardTitle>
          <CardDescription>
            {step === "name"
              ? "Let's set up your organization to get started"
              : "Pick every trade your business does — you can switch between them anytime"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {step === "name" ? (
            <form onSubmit={handleNameNext} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="org-name">Organization Name</Label>
                <Input
                  id="org-name"
                  placeholder="Your Company Name"
                  value={orgName}
                  onChange={(e) => setOrgName(e.target.value)}
                  required
                  disabled={loading}
                />
              </div>
              <Button type="submit" className="w-full">
                Continue
              </Button>
            </form>
          ) : (
            <div className="space-y-6">
              <TradePicker value={tradeCodes} onChange={setTradeCodes} disabled={loading} />
              <div className="flex justify-between">
                <Button variant="ghost" onClick={() => setStep("name")} disabled={loading}>
                  Back
                </Button>
                <Button onClick={handleCreate} disabled={loading || tradeCodes.length === 0}>
                  {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Create Organization
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default OrgSetup;
